// Generated mafia-noir art index (Higgsfield). Maps game data → asset URLs and
// provides a tiny preloaded-Image cache for canvas drawing on the City Map.

/** Every mob portrait in the pack, keyed by filename slug. Covers the 6 base
 *  archetypes AND all 48 family-styled units (FAMILY_UNITS type slugs) — drop
 *  a webp in mobs/ and it's live. */
const MOB_FILES = import.meta.glob('../assets/omerta/mobs/*.webp', {
  eager: true,
  import: 'default',
}) as Record<string, string>;

import docks from '../assets/omerta/districts/docks.webp';
import deadMansPier from '../assets/omerta/districts/dead-mans-pier.webp';
import ashlands from '../assets/omerta/districts/ashlands.webp';
import gallowsRow from '../assets/omerta/districts/gallows-row.webp';
import littleItaly from '../assets/omerta/districts/little-italy.webp';
import heights from '../assets/omerta/districts/heights.webp';
import foundry from '../assets/omerta/districts/foundry.webp';
import bloodAlley from '../assets/omerta/districts/blood-alley.webp';
import oldMarket from '../assets/omerta/districts/old-market.webp';
import freightyards from '../assets/omerta/districts/freightyards.webp';
import ginMile from '../assets/omerta/districts/gin-mile.webp';
import motorRow from '../assets/omerta/districts/motor-row.webp';

/** Unit type slug (or balance archetype) → enemy portrait. */
export const MOB_ART: Record<string, string> = Object.fromEntries(
  Object.entries(MOB_FILES).map(([path, url]) => [
    path.split('/').pop()!.replace('.webp', ''),
    url,
  ]),
);

/** District display name → aerial key-art. */
export const DISTRICT_ART: Record<string, string> = {
  'The Docks': docks,
  "Dead Man's Pier": deadMansPier,
  'The Ashlands': ashlands,
  'Gallows Row': gallowsRow,
  'Little Italy': littleItaly,
  'The Heights': heights,
  'The Foundry': foundry,
  'Blood Alley': bloodAlley,
  'The Old Market': oldMarket,
  'The Freightyards': freightyards,
  'Gin Mile': ginMile,
  'Motor Row': motorRow,
};

export const districtArt = (name?: string | null): string | undefined =>
  name ? DISTRICT_ART[name] : undefined;

// ── Equipment icons by slot/type ────────────────────────────────────────────
import weaponIcon from '../assets/omerta/items/weapon.webp';
import helmetIcon from '../assets/omerta/items/helmet.webp';
import armorIcon from '../assets/omerta/items/armor.webp';
import bootsIcon from '../assets/omerta/items/boots.webp';
import ringIcon from '../assets/omerta/items/ring.webp';
import amuletIcon from '../assets/omerta/items/amulet.webp';

import weaponLow from '../assets/omerta/items/weapon-low.webp';
import weaponHigh from '../assets/omerta/items/weapon-high.webp';
import armorLow from '../assets/omerta/items/armor-low.webp';
import armorHigh from '../assets/omerta/items/armor-high.webp';
import helmetLow from '../assets/omerta/items/helmet-low.webp';
import helmetHigh from '../assets/omerta/items/helmet-high.webp';

/** Artifact type → item icon (base / Rare tier). */
export const ITEM_ART: Record<string, string> = {
  WEAPON: weaponIcon,
  HELMET: helmetIcon,
  ARMOR: armorIcon,
  BOOTS: bootsIcon,
  RING: ringIcon,
  AMULET: amuletIcon,
};

/** Per-rarity-tier icons (low = Common/Uncommon, mid = Rare, high = Epic/Legendary). */
const ITEM_TIERS: Record<string, { low?: string; mid: string; high?: string }> = {
  WEAPON: { low: weaponLow, mid: weaponIcon, high: weaponHigh },
  ARMOR: { low: armorLow, mid: armorIcon, high: armorHigh },
  HELMET: { low: helmetLow, mid: helmetIcon, high: helmetHigh },
  BOOTS: { mid: bootsIcon },
  RING: { mid: ringIcon },
  AMULET: { mid: amuletIcon },
};
function tierOf(rarity?: string | null): 'low' | 'mid' | 'high' {
  if (rarity === 'COMMON' || rarity === 'UNCOMMON') return 'low';
  if (rarity === 'EPIC' || rarity === 'LEGENDARY') return 'high';
  return 'mid';
}

/**
 * Per-item art, keyed by the item's name slug (matches `items/item-defs.ts`).
 * Drop a `items/named/<slug>.webp` and it's live — no code change. Slugs must
 * match slugify(name) below.
 */
const NAMED_ITEM_ART = import.meta.glob<string>('../assets/omerta/items/named/*.webp', {
  eager: true, import: 'default', query: '?url',
});
const ITEM_ART_BY_SLUG: Record<string, string> = {};
for (const [path, url] of Object.entries(NAMED_ITEM_ART)) {
  const slug = path.split('/').pop()!.replace(/\.webp$/, '');
  ITEM_ART_BY_SLUG[slug] = url;
}

/** name → art slug. MUST match the backend def slug (drop apostrophes, non-alnum → '-'). */
export function itemSlug(name?: string | null): string {
  return (name ?? '')
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Item icon. Prefers the item's OWN art (resolved from its name), falling back
 * to the per-slot / per-rarity-tier generic icon for items without bespoke art
 * (older saved gear, or defs whose asset hasn't been generated yet).
 */
export const itemArt = (type?: string | null, rarity?: string | null, name?: string | null): string | undefined => {
  if (name) {
    const own = ITEM_ART_BY_SLUG[itemSlug(name)];
    if (own) return own;
  }
  if (!type) return undefined;
  const t = ITEM_TIERS[type];
  if (!t) return ITEM_ART[type];
  const tier = tierOf(rarity);
  return t[tier] ?? t.mid;
};

/**
 * 104px thumb of the same icon, for the SMALL renders (Market tiles 52px,
 * stash 44px, paperdoll, loot rows, toasts) — 256px originals were ~3x the
 * bytes those surfaces can display. 104 = 52 x 2, so retina stays sharp; the
 * container is fixed-size at every viewport, so a single 2x asset covers all
 * DPRs and no srcset is needed. Generated by scripts/make-item-thumbs.py; a
 * missing thumb falls back to the full icon, so the pipeline can't break art.
 * Big renders (ItemModal, ChestReveal at 168px) keep using itemArt().
 */
const NAMED_ITEM_THUMBS = import.meta.glob<string>('../assets/omerta/items/named/thumbs/*.webp', {
  eager: true, import: 'default', query: '?url',
});
const ITEM_THUMB_BY_SLUG: Record<string, string> = {};
for (const [path, url] of Object.entries(NAMED_ITEM_THUMBS)) {
  ITEM_THUMB_BY_SLUG[path.split('/').pop()!.replace(/\.webp$/, '')] = url;
}
export const itemThumb = (type?: string | null, rarity?: string | null, name?: string | null): string | undefined => {
  if (name) {
    const thumb = ITEM_THUMB_BY_SLUG[itemSlug(name)];
    if (thumb) return thumb;
  }
  return itemArt(type, rarity, name);
};

// ── Hero class portraits ────────────────────────────────────────────────────
import warriorArt from '../assets/omerta/crew-classes/warrior.webp';
import paladinArt from '../assets/omerta/crew-classes/paladin.webp';
import archerArt from '../assets/omerta/crew-classes/archer.webp';
import mageArt from '../assets/omerta/crew-classes/mage.webp';

/** HeroClass → crew portrait (mafia-reskinned archetypes). */
export const CLASS_ART: Record<string, string> = {
  WARRIOR: warriorArt,
  PALADIN: paladinArt,
  ARCHER: archerArt,
  MAGE: mageArt,
};
export const classArt = (cls?: string | null): string | undefined =>
  cls ? CLASS_ART[cls] : undefined;

// ── Combat fighter poses per class (idle/attack/heavy/ability/guard/hurt) ────
const FIGHTER_MODULES = import.meta.glob<string>('../assets/omerta/fighters/*.webp', {
  eager: true, import: 'default',
});
export type FighterPose = 'idle' | 'attack' | 'heavy' | 'ability' | 'guard' | 'hurt';
const FIGHTER_ART: Record<string, Partial<Record<FighterPose, string>>> = {};
for (const [path, url] of Object.entries(FIGHTER_MODULES)) {
  const m = path.match(/\/([a-z]+)-([a-z]+)\.webp$/);
  if (!m) continue;
  const cls = m[1].toUpperCase();
  (FIGHTER_ART[cls] ??= {})[m[2] as FighterPose] = url;
}
/** Pose art for a hero class (falls back through attack → idle → class portrait). */
export const fighterPose = (cls?: string | null, pose: FighterPose = 'idle'): string | undefined => {
  if (!cls) return undefined;
  const set = FIGHTER_ART[cls];
  return set?.[pose] || set?.attack || set?.idle || classArt(cls);
};

// ── Scene backgrounds ───────────────────────────────────────────────────────
import bgMarket from '../assets/omerta/backgrounds/market.webp';

import bgLogin from '../assets/omerta/backgrounds/login.webp';
import bgOffice from '../assets/omerta/backgrounds/office.webp';
import bgJobs from '../assets/omerta/backgrounds/jobs.webp';
import bgRankings from '../assets/omerta/backgrounds/rankings.webp';

import bgStudy from '../assets/omerta/backgrounds/study.webp';
import outVictory from '../assets/omerta/outcomes/victory.webp';
import outDefeat from '../assets/omerta/outcomes/defeat.webp';

/** Fight outcome banners. */
export const OUTCOME = { victory: outVictory, defeat: outDefeat };
export const outcomeArt = (won: boolean): string => (won ? outVictory : outDefeat);

// ── Rank medals (top-3) + tier badge ────────────────────────────────────────
import rankGold from '../assets/omerta/ranks/gold.webp';
import rankSilver from '../assets/omerta/ranks/silver.webp';
import rankBronze from '../assets/omerta/ranks/bronze.webp';
import rankTier from '../assets/omerta/ranks/tier.webp';

/** Medal for a leaderboard position (1-3), or undefined below the podium. */
export const rankMedal = (rank: number): string | undefined =>
  rank === 1 ? rankGold : rank === 2 ? rankSilver : rank === 3 ? rankBronze : undefined;

/** Generic era-tier rank pin. */
export const tierBadge = (): string => rankTier;

// ── Combat VFX sprites (on black — composite with mix-blend-mode: screen) ────
import fxMuzzle from '../assets/omerta/fx/muzzle.webp';
import fxSpark from '../assets/omerta/fx/spark.webp';
import fxBlood from '../assets/omerta/fx/blood.webp';
import fxAcid from '../assets/omerta/fx/acid.webp';

const FX_ART: Record<string, string> = { muzzle: fxMuzzle, spark: fxSpark, blood: fxBlood, acid: fxAcid };
export const fxArt = (kind?: string | null): string | undefined => (kind ? FX_ART[kind] : undefined);

import bgStash from '../assets/omerta/backgrounds/stash.webp';
import bgShop from '../assets/omerta/backgrounds/shop.webp';
import bgPit from '../assets/omerta/backgrounds/pit.webp';

export const BG = {
  market: bgMarket, login: bgLogin, office: bgOffice, study: bgStudy,
  jobs: bgJobs, rankings: bgRankings,
  stash: bgStash, shop: bgShop, pit: bgPit,
};

// ── Heist (dungeon) scene art by id ─────────────────────────────────────────
import heistSpeakeasy from '../assets/omerta/heists/speakeasy_raid.webp';
import heistDock from '../assets/omerta/heists/dock_heist.webp';
import heistBank from '../assets/omerta/heists/bank_job.webp';
import heistKingpin from '../assets/omerta/heists/kingpin_sitdown.webp';

export const HEIST_ART: Record<string, string> = {
  speakeasy_raid: heistSpeakeasy,
  dock_heist: heistDock,
  bank_job: heistBank,
  kingpin_sitdown: heistKingpin,
};
export const heistArt = (id?: string | null): string | undefined =>
  id ? HEIST_ART[id] : undefined;

// ── Family crests (by district name AND family name) ────────────────────────
import crestDoyle from '../assets/omerta/crests/doyle.webp';
import crestLedger from '../assets/omerta/crests/ledger.webp';
import crestSwitchmen from '../assets/omerta/crests/switchmen.webp';
import crestVelvet from '../assets/omerta/crests/velvet-chorus.webp';
import crestCarbide from '../assets/omerta/crests/carbide.webp';
import crestCinder from '../assets/omerta/crests/cinder.webp';
import crestGallows from '../assets/omerta/crests/gallows.webp';
import crestMoretti from '../assets/omerta/crests/moretti.webp';
import crestCommission from '../assets/omerta/crests/commission.webp';
import crestKowalski from '../assets/omerta/crests/kowalski.webp';
import crestRedLantern from '../assets/omerta/crests/red-lantern.webp';
import crestWidow from '../assets/omerta/crests/widow.webp';

/** Crest keyed by BOTH the district name and the family name it belongs to. */
export const CREST_ART: Record<string, string> = {
  'The Docks': crestDoyle, 'Doyle Crew': crestDoyle,
  'The Ashlands': crestCinder, 'Cinder Court': crestCinder,
  'Gallows Row': crestGallows, 'Gallows Boys': crestGallows,
  'Little Italy': crestMoretti, 'Moretti Family': crestMoretti,
  'The Heights': crestCommission, 'The Commission': crestCommission,
  'The Foundry': crestKowalski, 'Kowalski Syndicate': crestKowalski,
  'Blood Alley': crestRedLantern, 'Red Lantern Tong': crestRedLantern,
  'The Old Market': crestLedger, 'The Ledger Boys': crestLedger,
  'The Freightyards': crestSwitchmen, 'The Switchmen': crestSwitchmen,
  'Gin Mile': crestVelvet, 'Velvet Chorus': crestVelvet,
  'Motor Row': crestCarbide, 'The Carbide Club': crestCarbide,
  "Dead Man's Pier": crestWidow, "The Widow's Hand": crestWidow,
};
export const crestArt = (key?: string | null): string | undefined =>
  key ? CREST_ART[key] : undefined;

// ── Combat action scenes (mafia-noir cinematic backdrops for the fight action
//    bar buttons): alley gunfight, tommy-gun spray, taking cover, the ace, a
//    swig from the flask, the getaway car. ────────────────────────────────────
import scStrike from '../assets/omerta/combat/scenes/strike.webp';
import scHeavy from '../assets/omerta/combat/scenes/heavy.webp';
import scGuard from '../assets/omerta/combat/scenes/guard.webp';
import scAbility from '../assets/omerta/combat/scenes/ability.webp';
import scPatch from '../assets/omerta/combat/scenes/patch.webp';
import scFlee from '../assets/omerta/combat/scenes/flee.webp';

export const COMBAT_SCENE: Record<string, string> = {
  strike: scStrike, heavy: scHeavy, guard: scGuard,
  ability: scAbility, patch: scPatch, flee: scFlee,
};

// ── Shop consumable icons by effect kind ────────────────────────────────────
import shopNerve from '../assets/omerta/shop/nerve.webp';
import shopHealth from '../assets/omerta/shop/health.webp';
import shopXp from '../assets/omerta/shop/xp.webp';
import shopCash from '../assets/omerta/shop/cash.webp';
import shopRevive from '../assets/omerta/shop/revive.webp';
import shopChest from '../assets/omerta/shop/chest.webp';

/** Shop effect kind → icon. */
export const SHOP_ART: Record<string, string> = {
  nerve_refill: shopNerve,
  hp_refill: shopHealth,
  hp_partial: shopHealth,
  xp_booster: shopXp,
  cash_rush: shopCash,
  revive: shopRevive,
  chest_pull: shopChest,
  vault_artifact: weaponIcon,
  inventory_slot: shopChest,
};
export const shopArt = (kind?: string | null): string | undefined =>
  kind ? SHOP_ART[kind] : undefined;

// ── Preloaded Image cache (for <canvas> drawImage) ──────────────────────────
const cache = new Map<string, HTMLImageElement>();
function img(url: string): HTMLImageElement {
  let im = cache.get(url);
  if (!im) {
    im = new Image();
    im.src = url;
    cache.set(url, im);
  }
  return im;
}

/** Mob portrait URL — tries the family-styled unit slug first, then falls
 *  back to the base balance archetype (so a missing skin never blanks art). */
export const mobArt = (type?: string | null, archetype?: string | null): string | undefined =>
  (type ? MOB_ART[type] : undefined) ?? (archetype ? MOB_ART[archetype] : undefined);

/** A ready-to-draw mob portrait (slug → archetype fallback), or null. */
export function mobImg(type?: string, archetype?: string): HTMLImageElement | null {
  const url = mobArt(type, archetype);
  if (!url) return null;
  const im = img(url);
  return im.complete && im.naturalWidth > 0 ? im : (void img(url), null);
}

/** A ready-to-draw hero class portrait (for the map's player marker), or null. */
export function classImg(cls?: string | null): HTMLImageElement | null {
  const url = cls ? CLASS_ART[cls] : undefined;
  if (!url) return null;
  const im = img(url);
  return im.complete && im.naturalWidth > 0 ? im : (void img(url), null);
}

/** A ready-to-draw family crest (by district or family name), or null. */
export function crestImg(key?: string): HTMLImageElement | null {
  if (!key) return null;
  const url = CREST_ART[key];
  if (!url) return null;
  const im = img(url);
  return im.complete && im.naturalWidth > 0 ? im : (void img(url), null);
}

// ── Fight screen art ────────────────────────────────────────────────────────
// The dedicated bout screen: full-body class poses, the six pre-rendered action
// cards, the docks backdrop and the two HP-bar frames. Keyed by engine HeroClass
// (dir name lowercased): warrior=Enforcer, paladin=Capo, archer=Triggerman,
// mage=Fixer. Card art has its number AND title baked in, so the order is fixed
// (1 STRIKE · 2 HEAVY · 3 GUARD · 4 FLEE · 5 class ability · 6 PATCH UP) and no
// text is drawn over it.
const FIGHT_MODULES = import.meta.glob<string>('../assets/omerta/fight/**/*.webp', {
  eager: true, import: 'default',
});
export type FightPose =
  | 'default' | 'strike' | 'heavy' | 'guard' | 'flee'
  | 'ability' | 'patch-up' | 'damage' | 'damage-2' | 'win' | 'defeat';

const FIGHT_ART: Record<string, Record<string, string>> = {};
const FIGHT_SHARED: Record<string, string> = {};
for (const [path, url] of Object.entries(FIGHT_MODULES)) {
  const perClass = path.match(/\/fight\/([a-z]+)\/([a-z0-9-]+)\.webp$/);
  if (perClass) {
    (FIGHT_ART[perClass[1].toUpperCase()] ??= {})[perClass[2]] = url;
    continue;
  }
  const shared = path.match(/\/fight\/([a-z-]+)\.webp$/);
  if (shared) FIGHT_SHARED[shared[1]] = url;
}

/** Pose art for a class on the fight screen. Falls back to the idle `default`
 *  pose — only warrior/archer ship a second damage frame. */
export const fightPose = (cls?: string | null, pose: FightPose = 'default'): string | undefined => {
  if (!cls) return undefined;
  const set = FIGHT_ART[cls.toUpperCase()];
  return set?.[pose] ?? set?.default;
};

/** One of the six action cards (1-6) for a class. */
export const fightCard = (cls?: string | null, n = 1): string | undefined =>
  cls ? FIGHT_ART[cls.toUpperCase()]?.[`card-${n}`] : undefined;

export const FIGHT_BG = FIGHT_SHARED.docks;

/**
 * HP bars, cut into two layers. `home` has its portrait socket on the LEFT (the
 * hero), `guest` on the RIGHT (the opponent) — mirrored, not interchangeable.
 *
 * ONE ornate art-deco frame (generated 2026-07-16, replacing the old scratched
 * plates with their portrait circles) shared by both fighters — the guest side
 * mirrors it in CSS. The interior groove is empty; the depleting fill is plain
 * CSS layered into it. `groove` is the measured inset of that empty channel, in
 * % of the frame image (see FightScreen), so the fill sits exactly in the art.
 */
export const HP_BAR = {
  frame: FIGHT_SHARED['hp-frame'],
  /** frame is 1414x140; groove pixel box x143..1273, y28..110 */
  aspect: '1414 / 140' as const,
  groove: { left: 10.11, right: 9.9, top: 20.0, bottom: 20.71 },
};
